import { Trans } from "@lingui/macro";
import Image, { StaticImageData } from "next/image";
import React from "react";
import styled from "styled-components";
import mediaQueries from "~/guidelines/Theme/mediaQueries";
import { body1, heading2, subtitle2 } from "~/guidelines/Typography";

const Figure = styled.figure`
  display: flex;
  flex-direction: column;
  justify-self: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  width: 95px;
  margin: 0;
  padding: 0;

  ${mediaQueries.md()} {
    width: 115px;
  }
`;

const Content = styled.div`
  display: block;
  width: 100%;
  aspect-ratio: 1;

  ${mediaQueries.md()} {
    width: 115px;
  }
`;

const Caption = styled.figcaption`
  display: flex;
  text-align: center;
  ${subtitle2};
`;

type Feature = {
  image: StaticImageData;
  label: string;
};

type SharedSpaceFeatureProps = Feature;

const SharedSpaceFeature = ({ image, label }: SharedSpaceFeatureProps) => {
  return (
    <Figure>
      <Content>
        <Image
          src={image}
          sizes="10vw"
          style={{ width: "100%", height: "auto" }}
          quality={95}
          alt={label}
        />
      </Content>
      <Caption>{label}</Caption>
    </Figure>
  );
};

const Section = styled.section<{ backgroundColor?: string }>`
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor || theme.color.background.lightSecond};
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(9)};
  max-width: 1240px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing(7, 5)};

  ${mediaQueries.xl()} {
    flex-direction: row;
    justify-content: space-between;
    padding: ${({ theme }) => theme.spacing(17, 0)};
    gap: 170px;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 0;

  ${mediaQueries.xl()} {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const Title = styled.div`
  ${heading2};
  padding-bottom: ${({ theme }) => theme.spacing(5)};

  ${mediaQueries.md()} {
    padding-bottom: ${({ theme }) => theme.spacing(6)};
  }
`;

const Description = styled.div`
  ${body1}
`;

const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing(5)};

  ${mediaQueries.md()} {
    gap: ${({ theme }) => theme.spacing(8)};
  }

  ${mediaQueries.xl()} {
    flex: 1 1 0;
    min-width: 0;
  }
`;

export type SharedSpaceFeaturesProps = {
  features: Array<SharedSpaceFeatureProps>;
  backgroundColor?: string;
};

const SharedSpaceFeatures = ({
  features,
  backgroundColor,
}: SharedSpaceFeaturesProps) => {
  return (
    <Section style={{ backgroundColor }}>
      <Inner>
        <LeftPanel>
          <Title>
            <Trans>Everything you need, without the waste</Trans>
          </Title>
          <Description>
            <Trans>
              At Encore Mieux, we provide what’s genuinely useful during your
              stay, without cluttering your room.
              <br />
              Coffee, tea, a forgotten toothbrush or a clothes iron: everything
              is available in the hotel, self-serve. Reusable and simple.
            </Trans>
          </Description>
        </LeftPanel>

        <Features>
          {features.map((feature) => (
            <SharedSpaceFeature key={feature.label} {...feature} />
          ))}
        </Features>
      </Inner>
    </Section>
  );
};

export default SharedSpaceFeatures;
